import { useMemo, useState } from "react";

// Reusable history table shared by every monthly challenge card (ReBet,
// Referral, Casino, ...). `columns` are the header labels (unique) and each
// row supplies `cells` in matching order. Cells are ReactNodes so a caller can
// render custom content (badges, timestamps, etc.) per column.
//
// Sorting is opt-in: pass `sortable` and give each sortable column a `sortKey`
// (columns become `{ label, sortKey }` objects). Rows then provide a
// `sortValues` map keyed by those sortKeys with the raw comparable value, so
// sorting works even when a cell renders a custom ReactNode. Callers that pass
// plain string columns keep the previous read-only behaviour unchanged.

const ASCENDING = "asc";
const DESCENDING = "desc";
const NEUTRAL_CARET = "\u21C5";
const ASC_CARET = "\u2191";
const DESC_CARET = "\u2193";

const normalizeColumn = (column) =>
  typeof column === "string" ? { label: column } : column;

const compareValues = (a, b) => {
  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }
  return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const MonthlyHistoryTable = ({
  columns = [],
  rows = [],
  emptyText,
  rowTestIdPrefix,
  sortable = false,
  defaultSort = null,
  testId = "monthly-history-table",
}) => {
  const [sortState, setSortState] = useState(defaultSort);

  const normalizedColumns = useMemo(
    () => columns.map(normalizeColumn),
    [columns],
  );

  const sortedRows = useMemo(() => {
    if (!sortable || !sortState?.key) {
      return rows;
    }
    const direction = sortState.direction === DESCENDING ? -1 : 1;
    return [...rows].sort(
      (first, second) =>
        direction *
        compareValues(
          first.sortValues?.[sortState.key],
          second.sortValues?.[sortState.key],
        ),
    );
  }, [rows, sortable, sortState]);

  if (rows.length === 0) {
    return (
      <div
        className="user-manager-game-pass-monthly-history-empty"
        data-testid={`${testId}-empty`}
      >
        {emptyText}
      </div>
    );
  }

  const toggleSort = (key) =>
    setSortState((prev) => {
      if (prev?.key !== key) {
        return { key, direction: ASCENDING };
      }
      return {
        key,
        direction: prev.direction === ASCENDING ? DESCENDING : ASCENDING,
      };
    });

  const renderHeader = (column, index) => {
    const isSortable = sortable && !!column.sortKey;
    const isActive = isSortable && sortState?.key === column.sortKey;
    let ariaSort;
    let caret = NEUTRAL_CARET;
    if (isActive) {
      const ascending = sortState.direction === ASCENDING;
      ariaSort = ascending ? "ascending" : "descending";
      caret = ascending ? ASC_CARET : DESC_CARET;
    }

    return (
      <th key={`${column.label}-${index}`} aria-sort={ariaSort}>
        {isSortable ? (
          <button
            type="button"
            className="user-manager-game-pass-monthly-th user-manager-game-pass-monthly-th--sortable"
            onClick={() => toggleSort(column.sortKey)}
            data-testid={`${testId}-sort-${column.sortKey}`}
          >
            {column.label}
            <span
              className="user-manager-game-pass-monthly-th-sort"
              aria-hidden="true"
            >
              {caret}
            </span>
          </button>
        ) : (
          <span className="user-manager-game-pass-monthly-th">
            {column.label}
            <span
              className="user-manager-game-pass-monthly-th-sort"
              aria-hidden="true"
            >
              {NEUTRAL_CARET}
            </span>
          </span>
        )}
      </th>
    );
  };

  return (
    <div
      className="user-manager-game-pass-monthly-table-scroll"
      data-testid={testId}
    >
      <table className="user-manager-game-pass-monthly-table">
        <thead>
          <tr>{normalizedColumns.map(renderHeader)}</tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            const rowClassName = [
              row.rowClassName,
              row.variant
                ? `user-manager-game-pass-monthly-table-row--${row.variant}`
                : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <tr
                key={row.id}
                className={rowClassName || undefined}
                data-testid={
                  rowTestIdPrefix ? `${rowTestIdPrefix}-${row.id}` : undefined
                }
              >
                {row.fullWidth ? (
                  <td colSpan={normalizedColumns.length}>{row.cells[0]}</td>
                ) : (
                  row.cells.map((cell, index) => (
                    <td key={normalizedColumns[index]?.label ?? index}>
                      {cell}
                    </td>
                  ))
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MonthlyHistoryTable;
